import { useCallback, useEffect, useRef, useState } from "react"

import { wowMoneyApi } from "@shared/apis"
import { SECOND } from "@shared/constants"
import { useAccount } from "@shared/contexts"
import { createIdempotencyKey, store } from "@shared/modules"

import {
  hasReachedMaxPrice,
  WOW_MONEY_INTERVAL,
  WOW_MONEY_MAX_PRICE,
  WowMoneyNotification,
  WowMoneyNotificationType,
  WowMoneyPopupType,
} from "live/constants"

import {
  AccrualFailureAction,
  classifyHttpError,
  classifyRejected,
} from "./classifyAccrualResult"
import restoreCycleStartedAt, { SESSION_KEY } from "./restoreCycleStartedAt"

/**
 * 적립 시각이 됐는지 확인하는 주기.
 * setTimeout 한 번으로 1시간을 재면 절전·백그라운드 진입에 밀리므로,
 * 짧은 주기로 깨어나 실제 시각을 다시 비교한다.
 */
const CHECK_INTERVAL = 20 * SECOND

/**
 * 같은 Idempotency-Key 로 다시 보내는 최대 횟수.
 * 서버는 처리 중인 건을 120초 뒤에 가로채므로, 20초 간격으로 5번이면 그 안에 결과를 받는다
 */
const MAX_SAME_KEY_RETRY_COUNT = 5

type NotifyPayload = Pick<WowMoneyNotification, "price" | "message"> & {
  /** 서버 로그와 대조하기 위한 요청 키 */
  transactionId?: string
}

interface Options {
  /** 편성·방송 상태상 적립 기능이 동작할 수 있는지 */
  isAvailable?: boolean
  /**
   * 적립 요청 직전에 방송이 종료됐는지 서버에서 다시 확인한다.
   * 종료 상태는 Firebase 이벤트로만 반영돼서, 이벤트를 놓치면 종료된 방송에도 타이머가 계속 돈다
   */
  checkScheduleEnded?: () => Promise<boolean>
}

const useWowMoney = (
  openPopup: (type: WowMoneyPopupType) => void,
  { isAvailable = true, checkScheduleEnded }: Options = {},
) => {
  const {
    account: { viewer },
  } = useAccount()
  const isStartedRef = useRef<boolean>(false)
  const timerRef = useRef<NodeJS.Timeout>()
  const retryCountRef = useRef<number>(0)
  /** 한도 도달 등으로 주기를 멈춘 상태. 온라인 복구 시 다시 시작하지 않는다 */
  const isStoppedRef = useRef<boolean>(false)
  /** 한 적립 시도의 Idempotency-Key. 서버가 지급 여부를 아직 모르는 재요청에서만 유지한다 */
  const idempotencyKeyRef = useRef<string>()
  const notificationIdRef = useRef<number>(0)
  /** 첫 주기 타이머가 옛 addWowMoney 를 붙잡고 있어도 최신 확인 함수를 쓰도록 ref 로 들고 있는다 */
  const checkScheduleEndedRef = useRef(checkScheduleEnded)
  checkScheduleEndedRef.current = checkScheduleEnded
  const [isAccrualTarget, setIsAccrualTarget] = useState<boolean>(false)
  const [cycleStartedAt, setCycleStartedAt] = useState<number>()
  const [cumulativePrice, setCumulativePrice] = useState<number>()
  const [notification, setNotification] = useState<WowMoneyNotification>()

  const isAnonymous = viewer?.role === "ANONYMOUS"

  const notify = useCallback(
    (type: WowMoneyNotificationType, payload: NotifyPayload = {}) => {
      notificationIdRef.current += 1

      setNotification({ id: notificationIdRef.current, type, ...payload })
    },
    [],
  )

  const clearNotification = useCallback(() => {
    setNotification(undefined)
  }, [])

  const clearTimer = useCallback(() => {
    if (!timerRef.current) return

    clearInterval(timerRef.current)
    timerRef.current = undefined
  }, [])

  /** dueTimestamp 가 지나면 callback 을 한 번만 실행한다 */
  const runAt = useCallback(
    (dueTimestamp: number, callback: () => void) => {
      clearTimer()

      timerRef.current = setInterval(() => {
        if (Date.now() < dueTimestamp) return

        clearTimer()
        callback()
      }, CHECK_INTERVAL)
    },
    [clearTimer],
  )

  /** 주기 시작 시각을 갱신하고 세션에 남긴다 */
  const updateCycleStartedAt = useCallback((timestamp: number = Date.now()) => {
    store.session.set(SESSION_KEY.CYCLE_STARTED_AT, timestamp)
    setCycleStartedAt(timestamp)

    return timestamp
  }, [])

  const addWowMoney = useCallback(async () => {
    /** 지금부터 다시 1시간을 잰다 */
    const restartCycle = () => {
      runAt(updateCycleStartedAt() + WOW_MONEY_INTERVAL, addWowMoney)
    }

    /** 한도를 채웠으니 다음 주기를 걸지 않는다 */
    const complete = () => {
      isStoppedRef.current = true
      setCumulativePrice(WOW_MONEY_MAX_PRICE)
      notify("COMPLETE")
      openPopup("COMPLETE")
    }

    const handleFailure = (
      action: AccrualFailureAction,
      payload: NotifyPayload,
    ) => {
      if (action === "RETRY_SAME_KEY") {
        // 서버가 지급 여부를 아직 모른다. 같은 키를 유지한 채 다음 확인 주기에 다시 보낸다
        if (retryCountRef.current < MAX_SAME_KEY_RETRY_COUNT) {
          retryCountRef.current += 1
          runAt(Date.now(), addWowMoney)
          return
        }

        // 끝내 결과를 받지 못했다. 서버가 대사로 확정하므로 다음 주기로 넘어간다
        action = "RESTART_CYCLE"
      }

      idempotencyKeyRef.current = undefined
      retryCountRef.current = 0

      if (action === "COMPLETE") {
        complete()
        return
      }

      // 적립 실패는 사용자에게 노출하지 않고 콘솔에만 남긴다
      console.error("[WowMoney] 적립 실패", payload)

      if (action === "STOP") {
        isStoppedRef.current = true
        // 다음 주기를 걸지 않으니 옛 시작 시각을 남겨 두면 남은 시간이 0 이하인 채로
        // 상단바가 "적립 중"에 머문다. 시작 시각을 비워 "-" 로 보이게 한다
        setCycleStartedAt(undefined)
        return
      }

      restartCycle()
    }

    // 종료된 방송이면 키를 만들기 전에 멈춘다. 확인 자체가 실패하면 적립을 막지 않는다
    const isEnded = await checkScheduleEndedRef
      .current?.()
      .catch(() => false)

    if (isEnded) {
      isStoppedRef.current = true
      idempotencyKeyRef.current = undefined
      retryCountRef.current = 0
      setCycleStartedAt(undefined)
      return
    }

    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = createIdempotencyKey()
    }

    const idempotencyKey = idempotencyKeyRef.current

    try {
      const { data } = await wowMoneyApi.add(idempotencyKey)

      if (!data.isSuccess) {
        // HTTP 200 이지만 서버가 실패를 알려준 경우(예: statusCode 1003 존재하지 않는 회원)
        handleFailure(classifyRejected(data.statusCode), {
          message: data.message,
          transactionId: data.transactionId,
        })
        return
      }

      idempotencyKeyRef.current = undefined
      retryCountRef.current = 0

      setCumulativePrice(data.cumulativePrice)

      if (hasReachedMaxPrice(data.cumulativePrice)) {
        complete()
        return
      }

      notify("ACCUMULATE", { price: data.price })
      openPopup("ACCUMULATE")
      restartCycle()
    } catch (e) {
      const message = e?.response?.data?.message

      handleFailure(classifyHttpError(e?.response?.status, message), {
        message,
        transactionId: idempotencyKey,
      })
    }
  }, [openPopup, notify, runAt, updateCycleStartedAt])

  /**
   * 적립 대상인지 확인한다. 이번 달 한도를 이미 채웠으면 대상이 아니다.
   * 적립이 시작된 뒤에는 다시 부르지 않는다 - 도중에 한도에 도달해도
   * 완료 안내까지는 보여줘야 한다
   */
  const checkAccrualTarget = useCallback(async () => {
    if (isAnonymous) {
      // 로그인만 하면 적립 대상이므로 안내는 그대로 보여준다
      setIsAccrualTarget(true)
      return
    }

    try {
      const { data } = await wowMoneyApi.currentPrice()

      setCumulativePrice(data)
      setIsAccrualTarget(!hasReachedMaxPrice(data))
    } catch {
      // 조회에 실패하면 주기를 시작할 수 없다. 재접속 시 다시 시도한다
      setIsAccrualTarget(false)
    }
  }, [isAnonymous])

  useEffect(() => {
    if (!isAvailable) return

    checkAccrualTarget()
  }, [isAvailable, checkAccrualTarget])

  // 첫 적립 주기를 시작한다. 새로고침이면 직전 주기 시작 시각을 이어받는다
  useEffect(() => {
    if (isAnonymous || !isAccrualTarget || !isAvailable || isStartedRef.current)
      return

    isStartedRef.current = true

    const startedAt = updateCycleStartedAt(restoreCycleStartedAt())

    store.session.set(SESSION_KEY.REFRESHED_AT, 0)
    runAt(startedAt + WOW_MONEY_INTERVAL, addWowMoney)
  }, [
    isAnonymous,
    isAccrualTarget,
    isAvailable,
    addWowMoney,
    runAt,
    updateCycleStartedAt,
  ])

  // 오프라인 상태에서는 적립을 시도하지 않는다.
  // 복구되면 기존 만료 시각(주기 시작 + 1시간)으로 이어서 잰다
  useEffect(() => {
    if (!isAvailable) return

    const stopAccrual = () => {
      clearTimer()
    }

    const resumeAccrual = () => {
      if (isStoppedRef.current) return

      // 주기가 시작조차 못한 경우(입장 직후 끊겨 한도 조회가 실패한 경우).
      // 조회가 성공해야 최초 시작 effect 가 돌므로 거기서부터 다시 시작한다
      if (!isStartedRef.current || !cycleStartedAt) {
        checkAccrualTarget()
        return
      }

      runAt(cycleStartedAt + WOW_MONEY_INTERVAL, addWowMoney)
    }

    window.addEventListener("offline", stopAccrual)
    window.addEventListener("online", resumeAccrual)

    return () => {
      window.removeEventListener("offline", stopAccrual)
      window.removeEventListener("online", resumeAccrual)
    }
  }, [
    isAvailable,
    cycleStartedAt,
    clearTimer,
    runAt,
    addWowMoney,
    checkAccrualTarget,
  ])

  // 새로고침으로 시청 시간이 초기화되지 않도록 떠난 시각을 남긴다
  useEffect(() => {
    if (!isAvailable) return

    const markRefreshed = () => {
      store.session.set(SESSION_KEY.REFRESHED_AT, Date.now())
    }

    window.addEventListener("beforeunload", markRefreshed)

    return () => {
      window.removeEventListener("beforeunload", markRefreshed)
    }
  }, [isAvailable])

  useEffect(() => {
    if (isAvailable) return

    clearTimer()
  }, [isAvailable, clearTimer])

  // 화면을 떠나면 타이머도 멈춘다. 그대로 두면 다른 화면에서도 적립 요청이 나간다
  useEffect(() => clearTimer, [clearTimer])

  return {
    cycleStartedAt,
    isAccrualTarget,
    cumulativePrice,
    notification,
    clearNotification,
  }
}

export default useWowMoney
